import classNames from 'classnames';
import { Dispatch, FC, Fragment, ReactNode, SetStateAction, useId } from 'react';
import { TabButtons, TabItem } from './Tabs';

export interface PageLayoutProps<T> {
  title: ReactNode;
  headerClassname?: string;
  contentClassName?: string;
  classNameTitle?: string;
  tabs?: TabItem<T>[];
  activeTab?: string;
  tabNameParam?: string;
  setActiveTab?: Dispatch<SetStateAction<T>> | ((tab: T) => void);
  headerAction?: ReactNode;
  children?: ReactNode;
  headerBottomContent?: ReactNode;
  HeaderWrapper?: FC<{ children: ReactNode }>;
}

export const PageLayout = <T extends string>({
  tabs,
  title,
  children,
  activeTab,
  headerAction,
  setActiveTab,
  tabNameParam,
  HeaderWrapper = Fragment,
  headerBottomContent,
  headerClassname = '',
  contentClassName = '',
  classNameTitle = '',
}: PageLayoutProps<T>) => {
  const disabledTooltipId = useId();
  return (
    <div className="w-full h-full flex flex-col">
      <HeaderWrapper>
        <div
          className={classNames(
            'pt-4 px-3 sm:px-6 border-b border-[#e3e3e3] w-full flex flex-col gap-4',
            tabs ? 'pb-0' : 'pb-3',
            headerClassname,
          )}
        >
          <div
            className={classNames(
              'flex gap-2',
              {
                'flex-col sm:flex-row flex-wrap': !classNameTitle?.includes('flex-'),
                'sm:justify-between justify-start': !classNameTitle?.includes('justify-'),
                'sm:items-center': !classNameTitle?.includes('items-'),
              },
              classNameTitle,
            )}
          >
            <div className="text-[24px] leading-[32px] font-serif">{title}</div>
            {headerAction && (
              <div className="w-full sm:w-auto sm:ml-auto">
                {headerAction}
              </div>
            )}
          </div>
          {headerBottomContent}
          {tabs && (
            <TabButtons
              tabs={tabs}
              tabNameParam={tabNameParam}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              disabledTooltipId={disabledTooltipId}
              className="overflow-x-auto overflow-y-hidden h-[38px] sm:h-[34px]"
            />
          )}
        </div>
      </HeaderWrapper>
      <div
        id="scrollable"
        className={classNames(
          'flex flex-col w-full h-full relative overflow-y-auto',
          {
            'px-3 sm:px-6': !contentClassName?.includes('px-'),
            'py-4 sm:py-6': !contentClassName?.includes('py-'),
          },
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};
